import { useTranslation } from "react-i18next";
import { useState } from "react";
import { HelpCircle, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useApiRequest } from "@/hooks/use-api";

export function HelpWidget() {
  const { t } = useTranslation();
  const [message, setMessage] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { apiRequest } = useApiRequest();

  const MAX_MESSAGE_LENGTH = 1000;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!message.trim()) {
      return;
    }

    if (message.length > MAX_MESSAGE_LENGTH) {
      toast({
        title: t("components.helpWidget.messageTooLong"),
        description: `Message must be ${MAX_MESSAGE_LENGTH} characters or less`,
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await apiRequest("POST", "/api/help-request", {
        message: message.trim(),
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: t("components.helpWidget.helpRequestSent"),
          description:
            t("components.helpWidget.yourMessageHasBeen"),
        });
        setMessage("");
        setIsOpen(false);
      } else {
        toast({
          title: t("common.error"),
          description:
            data.error || "Failed to send help request. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error sending help request:", error);
      toast({
        title: t("common.error"),
        description: t("components.helpWidget.failedToSendHelp"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          title={t("components.helpWidget.getHelp")}
        >
          <HelpCircle className="h-4 w-4" />
        </Button>
      </DialogTrigger>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("components.helpWidget.sendForHelp")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mb-4">
            {t("components.helpWidget.sendAMessageTo")}
          </p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Textarea
                placeholder={t("components.helpWidget.describeYourIssueOr")}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="min-h-[100px]"
                maxLength={MAX_MESSAGE_LENGTH}
                required
              />
              <div className="text-xs text-muted-foreground text-right">
                {message.length}/{MAX_MESSAGE_LENGTH} characters
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={
                  !message.trim() ||
                  isSubmitting ||
                  message.length > MAX_MESSAGE_LENGTH
                }
              >
                <Send className="h-4 w-4 mr-2" />
                {isSubmitting ? t("components.helpWidget.sending") : t("components.helpWidget.sendForHelp")}
              </Button>
            </div>
          </form>
          <div className="mt-4 p-3 bg-muted rounded-md">
            <p className="text-sm text-muted-foreground">
              {t("components.helpWidget.needImmediateHelpWith")}
            </p>
          </div>
        </DialogContent>
    </Dialog>
  );
}
